import { __awaiter } from "tslib";
import elementResizeDetectorMaker from 'element-resize-detector';
import debounce from 'debounce';
import { NodeType, EditorHelper, Context, ContractHelper, PlaceholderHelper, } from '@getaccept/editor-lib-new';
import { openSansV29 } from '@getaccept/lib-shared-new/src/fonts/font-faces';
import { FontService } from '@getaccept/lib-shared-new/src/fonts/font-service';
import { storeToRefs } from 'pinia';
import { nextTick, defineComponent, computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { CommentsHelper } from '@getaccept/lib-shared-new/src/contextual-commenting/helpers/comments.helper';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-contextual-comment-button-state';
import DetachedCommentAnchor from '@getaccept/lib-shared-new/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { useApolloStore } from '../store/apollo.store';
import { useEditorViewInputStore } from '../store/editor-view-input.store';
import EditorSection from './EditorSection.vue';
export default defineComponent({
    name: 'EditorBlockView',
    components: {
        DetachedCommentAnchor,
        EditorSection,
    },
    props: {
        isPreview: { type: Boolean },
        content: { type: Object, default: undefined },
        focusedRequiredId: { type: String, default: undefined },
        mergeValues: { type: Object, default: undefined },
        isPdfViewer: { type: Boolean, default: false },
        isBlockToggled: { type: Boolean, default: false },
        blockId: { type: String, default: undefined },
        sender: { type: Object, default: undefined },
        theme: { type: Object, default: undefined },
        recipient: { type: Object, default: undefined },
        recipientInput: { type: Array, default: () => [] },
        context: { type: String, default: () => Context.Document },
        partiallySignedFields: { type: Array, default: () => [] },
        pusherClient: { type: Object, default: undefined },
        comments: { type: Array, default: () => [] },
        selectedCommentId: { type: String, default: '' },
        showContextualComments: { type: Boolean, default: false },
        createContextualCommentButtonState: {
            type: String,
            default: CreateContextualCommentButtonState.Enabled,
        },
        showCommentsLocked: { type: Boolean, default: false },
        currentUser: { type: Object, default: undefined },
        linkedContracts: { type: Array, default: () => [] },
        mappedPlaceholders: { type: Array, default: () => [] },
        allowedToEditAfterPartiallySigned: {
            type: Boolean,
        },
        apolloClient: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: [
        'node-click',
        'track-video',
        'observe-node-visibility',
        'observe-section-visibility',
        'partially-sign',
        'create-comment',
        'select-comment',
        'fetch-new-image',
        'alert',
        'update-recipient-input',
        'loaded',
    ],
    setup(props, { emit }) {
        const editorContainer = ref(null);
        const blockWidthClass = ref(null);
        const blockOffsetWidth = ref(0);
        const elementResizeDetector = ref(elementResizeDetectorMaker({
            strategy: 'scroll',
        }));
        const apolloStore = useApolloStore();
        const editorViewInputStore = useEditorViewInputStore();
        const { updatingInputs, failedInputUpdates, alert } = storeToRefs(editorViewInputStore);
        const sections = computed(() => { var _a; return (_a = props.content) === null || _a === void 0 ? void 0 : _a.sections; });
        const isPublicSite = computed(() => EditorHelper.isPublicContext(props.context));
        const filteredSections = computed(() => (sections.value || [])
            .filter(section => [Context.DsrTemplate, Context.DsrResource, Context.DsrPreview].includes(props.context)
            ? true
            : PlaceholderHelper.filterSectionForPlaceholders(section))
            .filter(section => ContractHelper.filterSectionByContractLinkAccess(section, props.recipient, props.currentUser)));
        const visibleSections = computed(() => [Context.DsrTemplate, Context.DsrPreview].includes(props.context)
            ? filteredSections.value
            : filteredSections.value.filter(({ hidden }) => !hidden));
        const firstRowContainsCoverImage = computed(() => {
            var _a;
            if (!((_a = sections.value) === null || _a === void 0 ? void 0 : _a[0])) {
                return false;
            }
            return EditorHelper.rowContainsCoverSize(sections.value[0].rows[0], NodeType.Image);
        });
        const lastRowContainsCoverImage = computed(() => {
            var _a;
            if (!((_a = sections.value) === null || _a === void 0 ? void 0 : _a[0])) {
                return false;
            }
            const { rows } = sections.value[0];
            return EditorHelper.rowContainsCoverSize(rows[rows.length - 1], NodeType.Image);
        });
        const sectionIds = computed(() => sections.value.map(section => section.id));
        const commentsWithoutSectionId = computed(() => props.comments.filter(comment => { var _a; return !sectionIds.value.includes((_a = comment.editorSelection) === null || _a === void 0 ? void 0 : _a.sectionId); }));
        const loadFont = (font, cssVariable) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            FontService.loadFont(font);
            yield nextTick();
            (_b = (_a = editorContainer.value) === null || _a === void 0 ? void 0 : _a.style) === null || _b === void 0 ? void 0 : _b.setProperty(cssVariable, font.fontFamily);
        });
        watch(alert, () => {
            if (!alert.value) {
                return;
            }
            emit('alert', alert.value);
            editorViewInputStore.setAlert(null);
        });
        watch(() => props.content.fontSettings, (fonts) => {
            var _a, _b, _c, _d;
            if (EditorHelper.shouldLoadFont(fonts === null || fonts === void 0 ? void 0 : fonts.defaultFont, isPublicSite.value, props.isPreview, (_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.fontSettings) === null || _b === void 0 ? void 0 : _b.defaultFont)) {
                loadFont((fonts === null || fonts === void 0 ? void 0 : fonts.defaultFont) || props.theme.fontSettings.defaultFont, '--editor-default-font-family');
            }
            if (EditorHelper.shouldLoadFont(fonts === null || fonts === void 0 ? void 0 : fonts.headingsFont, isPublicSite.value, props.isPreview, (_d = (_c = props.theme) === null || _c === void 0 ? void 0 : _c.fontSettings) === null || _d === void 0 ? void 0 : _d.headingsFont)) {
                loadFont((fonts === null || fonts === void 0 ? void 0 : fonts.headingsFont) || props.theme.fontSettings.headingsFont, '--editor-headings-font-family');
            }
        }, { immediate: true });
        watch(() => [props.apolloClient, props.recipient], () => {
            if (!props.recipient || !isPublicSite.value) {
                return;
            }
            apolloStore.setApolloClient(props.apolloClient);
        }, { immediate: true });
        const initFont = () => {
            var _a, _b, _c, _d;
            if ((_a = props.content.fontSettings) === null || _a === void 0 ? void 0 : _a.defaultFont) {
                return;
            }
            else if ((!isPublicSite.value && !((_c = (_b = props.theme) === null || _b === void 0 ? void 0 : _b.fontSettings) === null || _c === void 0 ? void 0 : _c.defaultFont)) ||
                !((_d = props.content.fontSettings) === null || _d === void 0 ? void 0 : _d.defaultFont)) {
                loadFont(openSansV29, '--editor-default-font-family');
            }
        };
        const setBlockWidth = (width) => {
            if (EditorHelper.isSlideShowLightboxVisible()) {
                return;
            }
            const blockWidth = EditorHelper.getBlockWidth(width);
            blockWidthClass.value = EditorHelper.getBlockWidthClass(blockWidth);
        };
        const updateBlockWidth = () => {
            var _a;
            blockOffsetWidth.value = ((_a = editorContainer.value) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 800;
            setBlockWidth(blockOffsetWidth.value);
        };
        const initResizeDetector = () => {
            if (!editorContainer.value) {
                return;
            }
            elementResizeDetector.value.listenTo(editorContainer.value, handleBlockResize);
        };
        const isDsr = computed(() => EditorHelper.isDealroomContext(props.context));
        const debounceUpdateBlockWidth = debounce(updateBlockWidth, 200);
        const handleBlockResize = ({ offsetWidth }) => {
            if (blockOffsetWidth.value === offsetWidth) {
                return;
            }
            debounceUpdateBlockWidth();
        };
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            yield nextTick();
            initResizeDetector();
            updateBlockWidth();
            initFont();
            emit('loaded');
        }));
        onBeforeUnmount(() => {
            var _a;
            (_a = elementResizeDetector.value) === null || _a === void 0 ? void 0 : _a.removeListener(editorContainer.value);
        });
        const handleUpdateInputValue = ({ id: fieldId, value }) => {
            emit('update-recipient-input', props.recipientInput.filter(input => input.fieldId !== fieldId).concat({ fieldId, value }));
            editorViewInputStore.updateInputValue({
                value,
                fieldId,
                blockId: props.blockId,
            });
        };
        const showCommentsBlockedBanner = computed(() => props.showContextualComments &&
            props.showCommentsLocked &&
            props.createContextualCommentButtonState ===
                CreateContextualCommentButtonState.SenderHasMadeChanges);
        return {
            commentsWithoutSectionId,
            editorContainer,
            blockWidthClass,
            loadFont,
            setBlockWidth,
            handleUpdateInputValue,
            updatingInputs,
            failedInputUpdates,
            firstRowContainsCoverImage,
            lastRowContainsCoverImage,
            sections,
            visibleSections,
            blockOffsetWidth,
            initResizeDetector,
            handleBlockResize,
            elementResizeDetector,
            isDsr,
            filterCommentsBySection: CommentsHelper.filterCommentsBySection,
            filteredSections,
            showCommentsBlockedBanner,
            t,
        };
    },
});
